import { useEffect, useState } from "react";
import { USER_ROLE } from "Constants/constant";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import { flattenGroupedPermissions, parseBooleanVal } from "Utils/utils";
import moment from "moment";
import { isGhostSessionActive, isPlatformTenantContextActive, readGhostSession } from "context/platformAdminContext.utils";
import { resolveRouteUserType, getSessionUser } from "pages/PlatformAdmin/Ghost/ghostSession.utils";
import { groupByPermissionsByName } from "Utils/utils";
import {
  BLK_PORTAL_PREMISSION_KEYS_CHILDREN,
  PERMISSION_KEYS,
  PERMISSION_KEYS_CHILDREN,
} from "store/constants";

/** Dispatched after role permissions are refreshed (Pusher or role save). */
export const REFRESH_PERMISSIONS_EVENT = "refreshPermissions";

export const notifyPermissionsRefreshed = () => {
  window.dispatchEvent(new Event(REFRESH_PERMISSIONS_EVENT));
};

/** Bump a version when permissions change so UI re-reads localStorage role gates. */
export const usePermissionsVersion = () => {
  const [permissionsVersion, setPermissionsVersion] = useState(0);

  useEffect(() => {
    const onRefresh = () => setPermissionsVersion((version) => version + 1);
    window.addEventListener(REFRESH_PERMISSIONS_EVENT, onRefresh);
    return () =>
      window.removeEventListener(REFRESH_PERMISSIONS_EVENT, onRefresh);
  }, []);

  return permissionsVersion;
};

export const DOCUMENT_PERMISSION_ACTIONS = {
  UPLOAD: "UPLOAD",
  FOLDER_CREATE: "FOLDER_CREATE",
  FOLDER_UPDATE: "FOLDER_UPDATE",
  FOLDER_DELETE: "FOLDER_DELETE",
  EDIT: "EDIT",
  DELETE: "DELETE",
};

const getParticipantDocumentPermission = (action) => {
  const doc = PERMISSION_KEYS_CHILDREN.PARTICIPANT.DOCUMEMT;
  switch (action) {
    case DOCUMENT_PERMISSION_ACTIONS.UPLOAD:
      return doc.UPLOAD;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_CREATE:
      return doc.FOLDER.CREATE;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_UPDATE:
      return doc.FOLDER.UPDATE;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_DELETE:
      return doc.FOLDER.DELETE;
    case DOCUMENT_PERMISSION_ACTIONS.EDIT:
      return doc.UPDATE;
    case DOCUMENT_PERMISSION_ACTIONS.DELETE:
      return doc.DELETE;
    default:
      return null;
  }
};

const getWorkerPortalDocumentPermission = (action) => {
  const doc = BLK_PORTAL_PREMISSION_KEYS_CHILDREN.DOCUMEMT;
  switch (action) {
    case DOCUMENT_PERMISSION_ACTIONS.UPLOAD:
      return doc.UPLOAD;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_CREATE:
      return doc.FOLDER.CREATE;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_UPDATE:
      return doc.FOLDER.UPDATE;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_DELETE:
      return doc.FOLDER.DELETE;
    default:
      return doc.UPLOAD;
  }
};

const getParticipantPortalDocumentPermission = (action) => {
  const doc = BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.DOCUMEMT;
  switch (action) {
    case DOCUMENT_PERMISSION_ACTIONS.UPLOAD:
      return doc.UPLOAD;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_CREATE:
      return doc.FOLDER.CREATE;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_UPDATE:
      return doc.FOLDER.UPDATE;
    case DOCUMENT_PERMISSION_ACTIONS.FOLDER_DELETE:
      return doc.FOLDER.DELETE;
    case DOCUMENT_PERMISSION_ACTIONS.EDIT:
      return doc.UPDATE;
    case DOCUMENT_PERMISSION_ACTIONS.DELETE:
      return doc.DELETE;
    default:
      return doc.UPLOAD;
  }
};

const getAdminManagerDocumentPermission = (role, action) => {
  if (role === "participant") {
    return {
      parent: PERMISSION_KEYS.PARTICIPANT,
      child: getParticipantDocumentPermission(action),
    };
  }
  if (role === "sil") {
    return { parent: PERMISSION_KEYS.SIL, child: null };
  }
  return {
    parent: PERMISSION_KEYS.HRM,
    child: PERMISSION_KEYS_CHILDREN.HRM.WORKER.SHOW,
  };
};

/**
 * Document permissions depend on profile context (participant vs worker/HRM vs SIL).
 * Worker documents must use HRM permissions, not participant permissions.
 */
export const canAccessDocumentAction = (role, action) => {
  const adminManagerPermission = getAdminManagerDocumentPermission(role, action);
  const adminManagerAccess = roleAccess(
    [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
    adminManagerPermission.parent,
    adminManagerPermission.child
  );

  if (role === "participant") {
    return (
      adminManagerAccess ||
      roleAccess(
        [USER_ROLE.Sp_Standard_User],
        PERMISSION_KEYS.PARTICIPANT,
        getParticipantPortalDocumentPermission(action)
      )
    );
  }

  return (
    adminManagerAccess ||
    roleAccess(
      [USER_ROLE.Sp_Standard_User, "sp_cos"],
      PERMISSION_KEYS.DOCUMENT,
      getWorkerPortalDocumentPermission(action)
    )
  );
};

const isPermissionGranted = (entry) =>
  parseBooleanVal(entry?.status) ||
  entry?.status === 1 ||
  entry?.status === "1" ||
  parseBooleanVal(entry?.isChecked) ||
  entry?.isChecked === 1;

/**
 * Frontline My Documents grid only (normal folders — not Required Documents table).
 * Required Documents keeps using canAccessDocumentAction / HRM gates unchanged.
 */
export const canAccessFrontlineMyDocumentsFolderAction = (action) => {
  if (!isFrontlineEmployeeUser()) {
    return false;
  }

  const permissionName = getWorkerPortalDocumentPermission(action);
  if (!permissionName) {
    return false;
  }

  if (hasSpAdminLevelAccess()) {
    return true;
  }

  // Same checks as Create Folder on this page + Employee Portal /documents route.
  if (canAccessDocumentAction("hrm", action)) {
    return true;
  }
  if (roleAccess([], PERMISSION_KEYS.DOCUMENT, permissionName)) {
    return true;
  }

  const currentUser = resolvePermissionUser();
  const group = currentUser?.permissions?.[PERMISSION_KEYS.DOCUMENT];
  if (Array.isArray(group)) {
    const child = group.find((entry) => entry?.name === permissionName);
    if (child && isPermissionGranted(child)) {
      return true;
    }
  }

  return hasExplicitSpPermission(permissionName);
};

export const STRIPE_CHECK_SUBSCRIPTION_STATUS = {
  SUBSCRIBED: "subscribed",
  PAYMENT_FAILED_TRAIL_EXISTS: "paymentFailedTrailExists",
  PAYMENTFAILED: "paymentFailed",
  RETRY_PAYMENT_FAILED: "retryPaymentFailed",
  TRAIL_EXPIRED: "trailExpired",
  TRAILING: "trialing",
  SUBSCRIPTION_UNPAID: "subscription_unpaid",
  PARTICIPANT_LIMIT_READ_ONLY: "participant_limit_read_only",
  PARTICIPANT_LIMIT_EXCEEDED: "participant_limit_exceeded",
};

const resolvePermissionUser = (currentUser) => {
  if (currentUser) {
    return currentUser;
  }

  if (isGhostSessionActive() && readGhostSession()?.actingUser) {
    return getSessionUser();
  }

  return GetItemFromLocalstorage("user");
};

/** SP owner (sp_admin) or tenant Super Admin manager — full portal access. */
export const hasSpAdminLevelAccessFromUser = (user) => {
  const currentUser = resolvePermissionUser(user);
  if (!currentUser) {
    return false;
  }

  if (parseBooleanVal(currentUser.is_manager_admin) === true) {
    return true;
  }

  const storedUserType = localStorage.getItem("userType");

  if (storedUserType === USER_ROLE.Sp_Admin) {
    return true;
  }

  if (
    currentUser.userType === USER_ROLE.Sp_Admin ||
    currentUser.user_type === USER_ROLE.Sp_Admin
  ) {
    return true;
  }

  if (
    isPlatformTenantContextActive() &&
    resolveRouteUserType() === USER_ROLE.Sp_Admin
  ) {
    return true;
  }

  return false;
};

export const hasSpAdminLevelAccess = (user) =>
  hasSpAdminLevelAccessFromUser(user);

/** Tenant Super Admin manager (role), not the SP account owner. */
export const isSuperAdminManager = (user) => {
  const currentUser = resolvePermissionUser(user);

  return parseBooleanVal(currentUser?.is_manager_admin) === true;
};

/** True SP owner only — not tenant Super Admin managers (they use role toggles). */
export const isTrueSpOwner = (user) => {
  const currentUser = resolvePermissionUser(user);
  if (!currentUser) {
    return false;
  }
  // ServiceProviderResource sets userType to sp_admin for Super Admin managers too;
  // is_manager_admin distinguishes them from the real SP owner.
  if (parseBooleanVal(currentUser.is_manager_admin) === true) {
    return false;
  }
  const storedUserType = localStorage.getItem("userType");
  return (
    storedUserType === USER_ROLE.Sp_Admin ||
    currentUser.userType === USER_ROLE.Sp_Admin ||
    currentUser.user_type === USER_ROLE.Sp_Admin
  );
};

/**
 * Instantly apply granted permission ids from a role_update broadcast.
 * Returns the updated user when this session's role was patched; otherwise null.
 */
export const applyRolePermissionIdsFromBroadcast = (roleId, permissionIds) => {
  if (roleId == null || !Array.isArray(permissionIds)) {
    return null;
  }

  const currentUser = resolvePermissionUser();
  if (!currentUser?.role_id || String(currentUser.role_id) !== String(roleId)) {
    return null;
  }

  // True SP owner has no granular toggles; Super Admin managers do.
  if (isTrueSpOwner(currentUser)) {
    return null;
  }

  const permissions = currentUser.permissions;
  if (!permissions || typeof permissions !== "object") {
    return null;
  }

  const granted = new Set(permissionIds.map((id) => String(id)));
  let changed = false;
  const nextPermissions = {};

  Object.keys(permissions).forEach((key) => {
    const group = permissions[key];
    if (!Array.isArray(group)) {
      nextPermissions[key] = group;
      return;
    }
    nextPermissions[key] = group.map((entry) => {
      if (entry?.id == null) {
        return entry;
      }
      const status = granted.has(String(entry.id));
      if (entry.status !== status) {
        changed = true;
      }
      return { ...entry, status };
    });
  });

  if (!changed) {
    return currentUser;
  }

  const updatedUser = { ...currentUser, permissions: nextPermissions };
  localStorage.setItem("user", JSON.stringify(updatedUser));
  notifyPermissionsRefreshed();
  return updatedUser;
};

const checkPermission = (
  roles,
  currentUser,
  parentPermissionKey,
  childPermissionName,
  isAuthenticateRole
) => {
  if (
    parentPermissionKey !== null &&
    hasSpAdminLevelAccessFromUser(currentUser)
  ) {
    return true;
  }

  let isRoleAuth = true;
  if (roles && Array.isArray(roles) && roles.length > 0) {
    isRoleAuth = isAuthenticateRole();
  }
  if (
    currentUser?.permissions &&
    Object.keys(currentUser?.permissions).length > 0 &&
    parentPermissionKey !== null &&
    isRoleAuth
  ) {
    const permissions = currentUser?.permissions || [];

    if (
      permissions &&
      permissions[parentPermissionKey] &&
      Object.keys(permissions[parentPermissionKey]).length === 1
    ) {
      return permissions[parentPermissionKey][0].status;
    }

    if (permissions && permissions[parentPermissionKey]) {
      const isParent = permissions[parentPermissionKey].find(
        (p) => p.parent_id === null
      );
      if (
        (isParent && childPermissionName === null) ||
        (isParent && isParent.status === false)
      ) {
        return isParent.status;
      }

      if (childPermissionName !== null && permissions[parentPermissionKey]) {
        const isAuth = permissions[parentPermissionKey].find(
          (per) => per.name === childPermissionName
        );
        return isAuth ? isAuth.status : true;
      }
    }

    return isGhostSessionActive() ? false : true;
  }

  if (
    isGhostSessionActive() &&
    parentPermissionKey !== null &&
    isRoleAuth &&
    resolveRouteUserType() !== USER_ROLE.Sp_Admin
  ) {
    return false;
  }

  if (
    currentUser &&
    (!Object.prototype.hasOwnProperty.call(currentUser, "permissions") ||
      Object.keys(currentUser.permissions || {}).length === 0) &&
    parentPermissionKey &&
    isRoleAuth &&
    (resolveRouteUserType() === USER_ROLE.Sp_Admin ||
      !isGhostSessionActive())
  ) {
    return true;
  }
  return isRoleAuth;
};

/**
 * Explicit Spatie permission status for a parent group (and optional child name).
 * Returns false when the permission row is missing — no wildcard from parent alone.
 */
export const getSpPermissionStatus = (
  parentPermissionKey,
  permissionName = null
) => {
  if (hasSpAdminLevelAccess()) {
    return true;
  }

  const currentUser = resolvePermissionUser();
  const permissions = currentUser?.permissions;

  if (!permissions || typeof permissions !== "object") {
    return false;
  }

  const group = permissions[parentPermissionKey];
  if (!Array.isArray(group) || group.length === 0) {
    return false;
  }

  if (permissionName === null || permissionName === undefined) {
    const parent = group.find((entry) => entry?.parent_id === null);
    return parent ? parseBooleanVal(parent.status) === true : false;
  }

  const child = group.find((entry) => entry?.name === permissionName);
  return child ? isPermissionGranted(child) : false;
};

/** Invoicing sidebar/module access requires at least one child permission enabled. */
export const canAccessInvoicingModule = () => {
  if (hasSpAdminLevelAccess()) {
    return true;
  }

  return (
    getSpPermissionStatus(
      PERMISSION_KEYS.ROSTER_INVOICE,
      PERMISSION_KEYS_CHILDREN.ROSTER_INVOICE.ALLOCATION
    ) ||
    getSpPermissionStatus(
      PERMISSION_KEYS.ROSTER_INVOICE,
      PERMISSION_KEYS_CHILDREN.ROSTER_INVOICE.CREATE
    )
  );
};

/** Leave requests share rostering access — there is no separate Spatie permission. */
export const ROSTERING_LEAVE_ACCESS_CHILDREN = [
  PERMISSION_KEYS_CHILDREN.ROSTERING.SCHEDULE.SHOW,
  PERMISSION_KEYS_CHILDREN.ROSTERING.TIMESHEET.SHOW,
  PERMISSION_KEYS_CHILDREN.ROSTERING.TIMESHEET_EXPORT.SHOW,
];

export const canAccessRosteringLeaveRequests = () => {
  if (hasSpAdminLevelAccess()) {
    return true;
  }

  return ROSTERING_LEAVE_ACCESS_CHILDREN.some((child) =>
    getSpPermissionStatus(PERMISSION_KEYS.ROSTERING, child)
  );
};

/** Payroll Settings page + Xero/MYOB/QuickBooks connect (matches /payroll-setting route gate). */
export const canAccessPayrollSettings = () => {
  if (isTrueSpOwner() || isSuperAdminManager()) {
    return true;
  }

  const currentUser = resolvePermissionUser();
  const permissions = currentUser?.permissions;

  if (!permissions || typeof permissions !== "object") {
    return false;
  }

  const group = permissions[PERMISSION_KEYS.ROSTERING];
  if (!Array.isArray(group) || group.length === 0) {
    return false;
  }

  const child = group.find(
    (entry) =>
      entry?.name === PERMISSION_KEYS_CHILDREN.ROSTERING.TIMESHEET_EXPORT.SHOW
  );

  return child ? isPermissionGranted(child) : false;
};

export const
    roleAccess = (
  roles,
  parentPermissionKey = null,
  childPermissionName = null
) => {
  const isAuthenticateRole = () => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return false;
    }

    let isAuthenticated = false;
    roles.forEach((role) => {
      if (
        isPlatformTenantContextActive() &&
        role === USER_ROLE.Sp_Admin
      ) {
        isAuthenticated = true;
        return;
      }

      if (
        isGhostSessionActive() &&
        role === resolveRouteUserType()
      ) {
        isAuthenticated = true;
        return;
      }

      const storedUserType = localStorage.getItem("userType");
      if (
        storedUserType &&
        currentUser &&
        role === storedUserType &&
        (currentUser.userType === storedUserType || !currentUser.userType)
      ) {
        isAuthenticated = true;
        return;
      }
    });

    return isAuthenticated;
  };

  const ghostSession = readGhostSession();
  const currentUser =
    isGhostSessionActive() && ghostSession?.actingUser
      ? getSessionUser()
      : GetItemFromLocalstorage("user");
  const userType = resolveRouteUserType();

  if (
    hasSpAdminLevelAccessFromUser(currentUser) &&
    roles?.includes(USER_ROLE.Sp_Admin)
  ) {
    return true;
  }

  return parentPermissionKey !== null || childPermissionName !== null
    ? checkPermission(
        roles,
        currentUser,
        parentPermissionKey,
        childPermissionName,
        isAuthenticateRole
      )
    : isAuthenticateRole();
};

/**
 * Platform Admin permission check using login payload `platform_permissions`.
 * Platform Owner (`is_platform_owner`) bypasses all checks.
 */
export const platformRoleAccess = (permissionName = null) => {
  const currentUser = GetItemFromLocalstorage("user");
  const userType = localStorage.getItem("userType");

  if (userType !== USER_ROLE.Super_Admin) {
    return false;
  }

  if (!permissionName) {
    return true;
  }

  const platformRoles = currentUser?.platform_roles || [];
  if (platformRoles.some((role) => role.is_platform_owner)) {
    return true;
  }

  const platformPermissions = currentUser?.platform_permissions;
  if (!platformPermissions || typeof platformPermissions !== "object") {
    return false;
  }

  for (const domain of Object.keys(platformPermissions)) {
    const permissions = platformPermissions[domain];
    if (!Array.isArray(permissions)) {
      continue;
    }

    const match = permissions.find((permission) => permission.name === permissionName);
    if (match) {
      return Boolean(match.status);
    }
  }

  return false;
};

export const platformRoleAccessAny = (permissionNames = []) => {
  if (!Array.isArray(permissionNames) || permissionNames.length === 0) {
    return platformRoleAccess();
  }

  return permissionNames.some((permissionName) =>
    platformRoleAccess(permissionName)
  );
};

/**
 * Entity Activity / dashboard "Recent Activity": same gating as sidebar Audit Log
 * (User Journey Tracking on + SP Admin or audit_log.view / audit_log).
 */
export const canViewAuditLogActivity = (spRosteringSetting) => {
  if (!parseBooleanVal(spRosteringSetting?.user_journey_tracking)) {
    return false;
  }
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return (
    roleAccess([], "audit_log", "audit_log.view") ||
    roleAccess([], "audit_log")
  );
};

const wyzedPermission = (key) =>
  PERMISSION_KEYS_CHILDREN.INTEGRATIONS.WYZED[key];

const WYZED_WORKER_TAB_PERMISSIONS = [
  wyzedPermission("VIEW_TRAINING"),
  wyzedPermission("SYNC_WORKER"),
  wyzedPermission("CREATE_WORKER"),
];

/**
 * True when the permission row exists and is explicitly granted.
 * Searches grouped keys and flattened entries so nested names like
 * integrations.wyzed.view_training are found reliably.
 */
export const hasExplicitSpPermission = (permissionNames) => {
  const names = Array.isArray(permissionNames)
    ? permissionNames
    : [permissionNames];
  const currentUser = resolvePermissionUser();
  const permissions = currentUser?.permissions;

  if (!permissions) {
    return false;
  }

  const isGranted = (entry) =>
    entry && names.includes(entry.name) && isPermissionGranted(entry);

  return flattenGroupedPermissions(permissions).some(isGranted);
};

/**
 * Medication Edit / Clone / Delete.
 * SP owner and tenant Super Admin always — the Super Admin role is non-editable,
 * so those toggles cannot be turned on. Other roles only when the matching toggle is ON.
 */
export const canAccessParticipantMedicationAction = (action) => {
  const permissionName =
    PERMISSION_KEYS_CHILDREN.PARTICIPANT?.MEDICATION?.[action] || null;

  if (!permissionName) {
    return false;
  }

  if (hasSpAdminLevelAccess()) {
    return true;
  }

  return hasExplicitSpPermission(permissionName);
};

/**
 * Worker profile Wyzed tab: any worker-scoped Wyzed permission (not org connect/disconnect alone).
 */
export const canAccessWyzedWorkerTab = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  if (hasExplicitSpPermission(WYZED_WORKER_TAB_PERMISSIONS)) {
    return true;
  }
  // Same pattern as audit log / other SP permission gates.
  return (
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("VIEW_TRAINING")) ||
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("SYNC_WORKER")) ||
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("CREATE_WORKER"))
  );
};

export const canViewWyzedTraining = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return (
    hasExplicitSpPermission(wyzedPermission("VIEW_TRAINING")) ||
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("VIEW_TRAINING"))
  );
};

export const canSyncWyzedWorker = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return (
    hasExplicitSpPermission(wyzedPermission("SYNC_WORKER")) ||
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("SYNC_WORKER"))
  );
};

export const canCreateWyzedWorker = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return (
    hasExplicitSpPermission(wyzedPermission("CREATE_WORKER")) ||
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("CREATE_WORKER"))
  );
};

export const canDownloadWyzedCertificate = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return (
    hasExplicitSpPermission(wyzedPermission("DOWNLOAD_CERTIFICATE")) ||
    roleAccess([], PERMISSION_KEYS.INTEGRATIONS, wyzedPermission("DOWNLOAD_CERTIFICATE")) ||
    hasExplicitSpPermission(
      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.WYZED.DOWNLOAD_CERTIFICATE
    ) ||
    roleAccess(
      [USER_ROLE.Sp_Standard_User],
      PERMISSION_KEYS.WYZED,
      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.WYZED.DOWNLOAD_CERTIFICATE
    )
  );
};

/** Black portal: worker can open the Wyzed training page */
export const canViewWorkerWyzedTraining = () => {
  const view = BLK_PORTAL_PREMISSION_KEYS_CHILDREN.WYZED.VIEW_TRAINING;
  return (
    hasExplicitSpPermission(view) ||
    getSpPermissionStatus(PERMISSION_KEYS.WYZED, view) ||
    // Parent-only grant (admin toggled "Wyzed Training" without each child)
    getSpPermissionStatus(PERMISSION_KEYS.WYZED) ||
    roleAccess([USER_ROLE.Sp_Standard_User], PERMISSION_KEYS.WYZED, view)
  );
};

/** Black portal: worker can open Start training link in Wyzed */
export const canStartWorkerWyzedTraining = () => {
  const start = BLK_PORTAL_PREMISSION_KEYS_CHILDREN.WYZED.START_TRAINING;
  return (
    hasExplicitSpPermission(start) ||
    getSpPermissionStatus(PERMISSION_KEYS.WYZED, start) ||
    getSpPermissionStatus(PERMISSION_KEYS.WYZED) ||
    roleAccess([USER_ROLE.Sp_Standard_User], PERMISSION_KEYS.WYZED, start)
  );
};

export const canManageWyzedIntegration = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return hasExplicitSpPermission([
    wyzedPermission("CONNECT"),
    wyzedPermission("DISCONNECT"),
  ]);
};

const SHIFT_NOTES_PERMISSION_NAMES = [
  "participant.shiftnote.list",
  "worker.can.view.shift_notes",
];

/**
 * Returns true only if at least one of the shift notes permissions is explicitly granted.
 * Used for Shift Notes tab so black portal (worker.can.view.shift_notes) and blue portal (participant.shiftnote.list) both work.
 * Searches ALL permission groups (participant, worker, etc.) since groupByPermissionsByName groups by name prefix.
 * When user has no permissions object (e.g. super admin), returns true for backward compatibility.
 */
export const hasShiftNotesAccess = () => {
  const currentUser = getSessionUser();
  const permissions = currentUser?.permissions;
  if (!permissions || typeof permissions !== "object" || Object.keys(permissions).length === 0) {
    return true;
  }
  for (const key of Object.keys(permissions)) {
    const group = permissions[key];
    if (!Array.isArray(group)) continue;
    const hasAccess = group.some(
      (p) =>
        p &&
        SHIFT_NOTES_PERMISSION_NAMES.includes(p.name) &&
        parseBooleanVal(p.status) === true
    );
    if (hasAccess) return true;
  }
  return false;
};

/**
 * Child permission within a dashboard group (cos / ah). Requires the parent
 * dashboard toggle to be on — prevents cross-module bleed from sibling groups.
 */
const getDashboardChildPermissionStatus = (
  parentPermissionKey,
  childPermissionName
) => {
  const currentUser = GetItemFromLocalstorage("user");
  const permissions = currentUser?.permissions;

  if (!permissions || typeof permissions !== "object") {
    return false;
  }

  const group = permissions[parentPermissionKey];
  if (!Array.isArray(group) || group.length === 0) {
    return false;
  }

  const parent = group.find((entry) => entry?.parent_id === null);
  if (!parent || parseBooleanVal(parent.status) !== true) {
    return false;
  }

  const child = group.find((entry) => entry?.name === childPermissionName);
  return child ? parseBooleanVal(child.status) === true : false;
};

/** SP Admin bypass; otherwise requires cos.casenote.delete on the user's role. */
export const canDeleteCosCaseNote = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return getDashboardChildPermissionStatus(
    PERMISSION_KEYS.COS,
    PERMISSION_KEYS_CHILDREN.COS.CASE_NOTES.DELETE
  );
};

/** SP Admin bypass; otherwise requires ah.casenote.delete on the user's role. */
export const canDeleteAhCaseNote = () => {
  if (roleAccess([USER_ROLE.Sp_Admin])) {
    return true;
  }
  return getDashboardChildPermissionStatus(
    PERMISSION_KEYS.ALLIED_HEALTH,
    PERMISSION_KEYS_CHILDREN.ALLIED_HEALTH.CASE_NOTES.DELETE
  );
};

/** Frontline portal: support worker or support coordinator (same API routes as sp_standard_user). */
export const isFrontlineEmployeeUser = () => {
  const userType = isGhostSessionActive()
    ? resolveRouteUserType()
    : localStorage.getItem("userType");
  return (
    userType === USER_ROLE.Sp_Standard_User || userType === "sp_cos"
  );
};

export const getBillingAccess = (user) => user?.payment_setting?.billing_access ?? null;

export const checkSubscriptionStatus = (user) => {
  if (!user) return;
  const { payment_setting } = user;
  if (!payment_setting) return;

  const participantLimit = payment_setting?.participant_limit_status;

  if (participantLimit?.read_only_due_to_participant_limit) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.PARTICIPANT_LIMIT_READ_ONLY;
  }
  if (participantLimit?.over_limit) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.PARTICIPANT_LIMIT_EXCEEDED;
  }

  const trialEndDate = moment(payment_setting?.trial_end_date, "YYYY-MM-DD");
  // const trialEndDate = moment("2024-02-15").format("YYYY-MM-DD");

  const today = moment().startOf("day");
  const isPast = moment(trialEndDate).isBefore(today, "day");

  // console.log("isPast isPast", isPast);

  const hasSubscriptionId =
    Object.prototype.hasOwnProperty.call(payment_setting, "subscription_id") &&
    payment_setting.subscription_id !== null;
  const currentSubscriptionStatus = payment_setting?.current_subscription_status;
  const isPayingTenant =
    parseBooleanVal(payment_setting?.subscription_status) === true && hasSubscriptionId;

  if (
    isPayingTenant &&
    (currentSubscriptionStatus === "active" ||
      currentSubscriptionStatus === "trialing" ||
      currentSubscriptionStatus === "past_due" ||
      currentSubscriptionStatus == null)
  ) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIBED;
  } else if (
    isPast === false &&
    parseBooleanVal(payment_setting?.subscription_status) === false &&
    Object.prototype.hasOwnProperty.call(payment_setting, "subscription_id") &&
    payment_setting.subscription_id !== null &&
    (payment_setting?.current_subscription_status === "incomplete" ||
      payment_setting.current_subscription_status === "incomplete_expired")
  ) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENT_FAILED_TRAIL_EXISTS;
  } else if (
    isPast === true &&
    parseBooleanVal(payment_setting?.subscription_status) === false &&
    Object.prototype.hasOwnProperty.call(payment_setting, "subscription_id") &&
    payment_setting.subscription_id !== null &&
    (payment_setting?.current_subscription_status === "incomplete" ||
      payment_setting.current_subscription_status === "incomplete_expired")
  ) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENTFAILED;
  } else if (
    isPast === true &&
    parseBooleanVal(payment_setting.subscription_status) === false &&
    Object.prototype.hasOwnProperty.call(payment_setting, "subscription_id") &&
    payment_setting.subscription_id !== null &&
    payment_setting.current_subscription_status !== null &&
    payment_setting.current_subscription_status === "unpaid" &&
    payment_setting.attempt_count === null
  ) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIPTION_UNPAID;
  } else if (
    isPast === true &&
    parseBooleanVal(payment_setting.subscription_status) === false &&
    Object.prototype.hasOwnProperty.call(payment_setting, "subscription_id") &&
    payment_setting.subscription_id !== null &&
    payment_setting.current_subscription_status !== null &&
    payment_setting.current_subscription_status === "past_due" &&
    payment_setting.attempt_count !== null &&
    payment_setting.attempt_count > 0
  ) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.RETRY_PAYMENT_FAILED;
  } else if (
    payment_setting?.on_trial === false ||
    payment_setting?.on_trial === 0 ||
    payment_setting?.on_trial === "0"
  ) {
    return STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAIL_EXPIRED;
  }

  return isPast
    ? STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAIL_EXPIRED
    : STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAILING;
};

export const isGracePeriodOver = (user) => {
  if (!user) return;
  const status = checkSubscriptionStatus(user);
  return status === STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIPTION_UNPAID;
};

/**
 * True when the SP UI should block creates/updates (same signal as LoginContext isUserGracePeriodOver).
 * Includes unpaid grace period and API-driven read_only (e.g. participant limit after warning period).
 */
export const isSpUiMutationsDisabled = (user) => {
  if (!user) return false;
  const billingAccess = getBillingAccess(user);
  if (billingAccess && typeof billingAccess.ui_mutations_disabled === "boolean") {
    return billingAccess.ui_mutations_disabled;
  }
  return (
    isGracePeriodOver(user) === true ||
    parseBooleanVal(user?.payment_setting?.read_only) === true
  );
};

/** True when user is over participant limit or in read-only due to limit (from payment_setting.participant_limit_status). */
export const isOverParticipantLimit = (user) => {
  const limitStatus = user?.payment_setting?.participant_limit_status;
  return limitStatus && (limitStatus.over_limit || limitStatus.read_only_due_to_participant_limit);
};

/** Statuses that lock the app to subscription/plans only (no access to rest of app). */
const LOCKOUT_STATUSES = [
  STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAIL_EXPIRED,
  STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENTFAILED,
  // Participant limit after 7 days: read-only via API (SpAdminCheckReadOnly), not full UI lockout — user can still browse.
];

/** True when subscription status should restrict user to subscription/plans routes only. */
export const shouldLockToSubscriptionPlans = (status) =>
  status != null && LOCKOUT_STATUSES.includes(status);

/** Prefer server billing_access when present; otherwise legacy classifier. */
export const shouldLockToSubscriptionPlansForUser = (user) => {
  const status = checkSubscriptionStatus(user);
  if (status === STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIBED) {
    return false;
  }
  const legacyLock = shouldLockToSubscriptionPlans(status);
  const billingAccess = getBillingAccess(user);
  if (billingAccess && typeof billingAccess.lock_to_plans === "boolean") {
    return billingAccess.lock_to_plans || legacyLock;
  }
  return legacyLock;
};

/** True when we should show the subscription/participant-limit banner (any of these statuses). */
const BANNER_STATUSES = [
  STRIPE_CHECK_SUBSCRIPTION_STATUS.TRAIL_EXPIRED,
  STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENTFAILED,
  STRIPE_CHECK_SUBSCRIPTION_STATUS.PARTICIPANT_LIMIT_EXCEEDED,
  STRIPE_CHECK_SUBSCRIPTION_STATUS.PARTICIPANT_LIMIT_READ_ONLY,
];

export const shouldShowSubscriptionBanner = (status) =>
  status != null && BANNER_STATUSES.includes(status);

/** Prefer server billing_access when present; otherwise legacy classifier. */
export const shouldShowSubscriptionBannerForUser = (user) => {
  const status = checkSubscriptionStatus(user);
  if (status === STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIBED) {
    return false;
  }
  const legacyBanner = shouldShowSubscriptionBanner(status);
  const billingAccess = getBillingAccess(user);
  if (billingAccess && typeof billingAccess.show_subscription_banner === "boolean") {
    return billingAccess.show_subscription_banner || legacyBanner;
  }
  return legacyBanner;
};

export const checkCurrentUserLoggedInByUUID = (uuid) => {
  const currentUser = GetItemFromLocalstorage("user");
  return currentUser?.uuid !== uuid;
};
